const bearerHeader = {
  key: "bearerkey",
  value: "XXXXXXXXXXXXXXXXXXXX",
  required: true,
  description:
    "Restaurant API key in the header. Keep it secret — requests without it fail.",
};

const jsonHeader = {
  key: "Content-Type",
  value: "application/json",
  required: true,
  description: "Request body format.",
};

const tokenField = {
  field: "token",
  type: "string",
  required: true,
  description: "JWT from Authorize. Required on every call after login.",
  example: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy",
};

const err = (code, label, message) => ({
  code,
  type: "error",
  label,
  example: `{\n  "status": false,\n  "status_code": ${code},\n  "message": "${message}"\n}`,
});

export const apis = [
  {
    id: "login",
    name: "Authorize",
    method: "POST",
    endpoint: "/authorize",
    description:
      "Creates a single-use JWT used to authenticate all later Wiyak Delivery API calls. Token lifespan is 365 days. Optionally registers an HTTPS webhook URL to receive order status updates.",
    headers: [bearerHeader, jsonHeader],
    body: [
      {
        field: "web_hook_url",
        type: "string",
        required: false,
        description:
          "HTTPS callback URL for order events. Must start with https://. Payload is the full order model (with driver info once accepted).",
        example: "https://yourrestaurant.com/webhook",
      },
    ],
    requestExample: `{
  "web_hook_url": "https://yourrestaurant.com/webhook"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Success",
        example: `{
  "status": true,
  "status_code": 200,
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy"
}`,
      },
      err(401, "Unauthorized", "This request is not authorized"),
      err(1000, "Unsecure Webhook", "Webhook URL does not have a secure protocol, it should be https."),
      err(1001, "Invalid Webhook URL", "The provided URL as a webhook is invalid."),
      err(1002, "Inactive Restaurant", "Your restaurant is currently disable, Please contact our support team for more details."),
      err(429, "Too Many Requests", "Too many requests hit the API too quickly."),
      err(500, "Server Error", "Internal Server Error"),
    ],
    notes: [
      "Save the token — it is required in the body of all subsequent API calls.",
      "Rate limit: 20 requests per second across your organization's API keys. Exceeding this returns 429.",
      "Prefer webhooks over polling for order state changes.",
      "Keep your bearerkey confidential — it is your permanent restaurant credential.",
    ],
  },

  {
    id: "estimated-fare",
    name: "Get Estimated Price",
    method: "POST",
    endpoint: "/getfare",
    description:
      "Returns the estimated delivery fare for a drop address. Uses branch_id as pickup (or the restaurant default branch if omitted) plus drop lat/long and pickup_date_time so quotes respect working hours. Does not fire webhooks.",
    headers: [bearerHeader, jsonHeader],
    body: [
      tokenField,
      { field: "lat", type: "number", required: true, description: "Drop latitude.", example: "29.3326" },
      { field: "long", type: "number", required: true, description: "Drop longitude.", example: "48.0719" },
      { field: "address", type: "string", required: true, description: "Drop address.", example: "Salmiya, Kuwait" },
      { field: "pickup_date_time", type: "string", required: true, description: 'Pickup time. Format: "YYYY-MM-DD HH:mm:ss".', example: "2026-09-18 17:28:33" },
      { field: "branch_id", type: "string", required: false, description: "Pickup branch. Omit to use the restaurant default branch. Recommended for vendor integrations.", example: "1d3c05a0-cf57-11ec-b00f-65a3fce43a38" },
    ],
    requestExample: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy",
  "lat": 29.3326,
  "long": 48.0719,
  "address": "Salmiya, Kuwait",
  "pickup_date_time": "2026-09-18 17:28:33",
  "branch_id": "1d3c05a0-cf57-11ec-b00f-65a3fce43a38"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Success",
        example: `{
  "status": true,
  "status_code": 200,
  "data": {
    "amount": 1.750,
    "currency": "KWD",
    "distance": 4.2,
    "duration": 12
  }
}`,
      },
      err(1002, "Inactive Restaurant", "Your restaurant is currently disable, Please contact our support team for more details."),
      err(1003, "Inactive Branch", "Your branch is currently disable, Please contact our support team for more details."),
      err(1004, "Out of Branch Area", "Unfortunately we don't offer delivery to this area for this branch. Please contact our support team for more details."),
      err(1005, "Delivery Unavailable", "Unfortunately we don't deliver in this area. Please contact our support team for more details."),
      err(1006, "Restricted Area", "The delivery address is restricted. We don't serve in that area."),
      err(1007, "No Delivery Cars", "Currently we don't have valid delivery car's for your delivery address."),
      err(1017, "Create Quote Failed", "Aw! Snap!, We have encountered an error creating your order. Please try again after some time."),
      err(500, "Server Error", "Internal Server Error"),
    ],
    notes: [
      "Date format is YYYY-MM-DD HH:mm:ss, for example 2026-01-01 12:00:00.",
      "Pass branch_id for an accurate quote for that restaurant location.",
      "This call does not return an order model and does not notify webhooks.",
    ],
  },

  {
    id: "branches",
    name: "Get All Branches",
    method: "POST",
    endpoint: "/branches",
    description:
      "Returns all active branches for the restaurant, each with id and name.",
    headers: [bearerHeader, jsonHeader],
    body: [tokenField],
    requestExample: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Success",
        example: `{
  "status": true,
  "status_code": 200,
  "data": [
    {
      "branch_id": "1d3c05a0-cf57-11ec-b00f-65a3fce43a38",
      "branch_name": "Salmiya Branch",
      "lat": 29.3326,
      "long": 48.0719,
      "address": "Salmiya, Kuwait"
    }
  ]
}`,
      },
      err(1002, "Inactive Restaurant", "Your restaurant is currently disable, Please contact our support team for more details."),
      err(1003, "Inactive Branch", "Your branch is currently disable, Please contact our support team for more details."),
      err(401, "Unauthorized", "This request is not authorized"),
      err(500, "Server Error", "Internal Server Error"),
    ],
    notes: [
      "Use branch_id from this response in Create Order, Get Estimated Price, and List Order.",
      "Only active branches are returned.",
    ],
  },

  {
    id: "create-branch",
    name: "Create Restaurant Branch",
    method: "POST",
    endpoint: "/create/restaurant/branch",
    description:
      "Creates a new restaurant branch. The branch is activated immediately with selected delivery areas so orders can be placed right away.",
    headers: [bearerHeader, jsonHeader],
    body: [
      tokenField,
      { field: "country_code", type: "string", required: true, description: "Branch country code with + prefix.", example: "+965" },
      { field: "branch_phone", type: "string", required: true, description: "Branch phone number.", example: "55001234" },
      { field: "branch_email", type: "string", required: true, description: "Branch email address.", example: "branch@restaurant.com" },
      { field: "branch_name", type: "string", required: true, description: "Branch display name.", example: "Salmiya Branch" },
      { field: "lat", type: "number", required: true, description: "Branch latitude.", example: "29.3326" },
      { field: "long", type: "number", required: true, description: "Branch longitude.", example: "48.0719" },
      { field: "branch_address", type: "string", required: true, description: "Branch street address.", example: "Salmiya, Kuwait" },
    ],
    requestExample: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy",
  "country_code": "+965",
  "branch_phone": "55001234",
  "branch_email": "branch@restaurant.com",
  "branch_name": "Salmiya Branch",
  "lat": 29.3326,
  "long": 48.0719,
  "branch_address": "Salmiya, Kuwait"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Branch Created",
        example: `{
  "status": true,
  "status_code": 200,
  "data": {
    "branch_id": "1d3c05a0-cf57-11ec-b00f-65a3fce43a38",
    "branch_name": "Salmiya Branch"
  }
}`,
      },
      err(1201, "Phone Exists", "Branch Phone number Already Exists"),
      err(1202, "Email Exists", "Branch Email Already Exists"),
      err(1203, "Restaurant Disabled", "Your restaurant is currently disable"),
      err(1204, "Location Not Served", "your location not available our Service"),
      err(500, "Server Error", "Internal Server Error"),
    ],
    notes: [
      "The new branch is activated immediately with selected delivery areas.",
      "branch_phone and branch_email must be unique.",
    ],
  },

  {
    id: "create-order",
    name: "Create Order",
    method: "POST",
    endpoint: "/createorder",
    description:
      "Dispatches a delivery order. Pickup is the given branch_id (or the restaurant main branch if omitted). Drop lat/long/address and customer name + phone are required. Returns the full order model and fires webhooks as the order progresses.",
    headers: [bearerHeader, jsonHeader],
    body: [
      tokenField,
      { field: "lat", type: "number", required: true, description: "Drop latitude.", example: "29.3759" },
      { field: "long", type: "number", required: true, description: "Drop longitude.", example: "47.9774" },
      { field: "address", type: "string", required: true, description: "Drop address.", example: "Kuwait City, Kuwait" },
      { field: "order_number", type: "string", required: true, description: "Your external order / POS number.", example: "1321" },
      { field: "order_details", type: "string", required: false, description: "Order notes shown to the driver (items, instructions).", example: "PIZZA" },
      { field: "user_name", type: "string", required: true, description: "Customer full name.", example: "Jack" },
      { field: "country_code", type: "string", required: true, description: "Customer country code with + prefix.", example: "+965" },
      { field: "user_phone", type: "string", required: true, description: "Customer mobile number without country code.", example: "1234567890" },
      { field: "user_email", type: "string", required: false, description: "Customer email.", example: "jack@example.com" },
      { field: "pickup_date_time", type: "string", required: true, description: 'Pickup time. Format: "YYYY-MM-DD HH:mm:ss".', example: "2026-09-18 17:28:33" },
      { field: "payment_type", type: "string", required: true, description: 'Payment type. When "COD", cod_amount is required.', example: "COD" },
      { field: "cod_amount", type: "string", required: false, description: 'Cash-on-delivery amount. Required when payment_type is "COD".', example: "50.99" },
      { field: "branch_id", type: "string", required: false, description: "Pickup branch. Omit to use the restaurant main branch.", example: "1d3c05a0-cf57-11ec-b00f-65a3fce43a38" },
    ],
    requestExample: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy",
  "lat": 29.3759,
  "long": 47.9774,
  "address": "Kuwait City, Kuwait",
  "order_number": "1321",
  "order_details": "PIZZA",
  "user_name": "Jack",
  "country_code": "+965",
  "user_phone": "1234567890",
  "user_email": "jack@example.com",
  "pickup_date_time": "2026-09-18 17:28:33",
  "payment_type": "COD",
  "cod_amount": "50.99",
  "branch_id": "1d3c05a0-cf57-11ec-b00f-65a3fce43a38"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Order Created",
        example: `{
  "status": true,
  "status_code": 200,
  "data": {
    "order_id": "629f2fe68fc53a6e738b0dbc",
    "display_book_id": "WD-1001",
    "status": "PENDING",
    "amount": "KWD 1.750",
    "order_number": "1321",
    "order_details": "PIZZA",
    "payment_type": "COD",
    "cod_amount": "50.99",
    "branch_id": "1d3c05a0-cf57-11ec-b00f-65a3fce43a38",
    "customer_info": [
      { "name": "Jack", "country_code": "+965", "phone": "1234567890", "email": "jack@example.com" }
    ],
    "pick_info": { "lat": 29.3326, "long": 48.0719, "address": "Salmiya Branch" },
    "drop_info": { "lat": 29.3759, "long": 47.9774, "address": "Kuwait City, Kuwait" },
    "driver_info": [{ "name": "", "phone": "", "location": { "latitude": 0, "longitude": 0 } }],
    "vehicle_info": [{ "car": "", "model": "", "brand": "", "color": "", "license_plate": "" }]
  }
}`,
      },
      err(1002, "Inactive Restaurant", "Your restaurant is currently disable, Please contact our support team for more details."),
      err(1003, "Inactive Branch", "Your branch is currently disable, Please contact our support team for more details."),
      err(1004, "Out of Branch Area", "Unfortunately we don't offer delivery to this area for this branch. Please contact our support team for more details."),
      err(1005, "Delivery Unavailable", "Unfortunately we don't deliver in this area. Please contact our support team for more details."),
      err(1006, "Restricted Area", "The delivery address is restricted. We don't serve in that area."),
      err(1007, "No Delivery Cars", "Currently we don't have valid delivery car's for your delivery address."),
      err(1008, "Low Wallet", "Your wallet balance is running low. We cannot accept any orders from your restaurant. Please recharge your wallet to proceed further."),
      err(1017, "Create Failed", "Aw! Snap!, We have encountered an error creating your order. Please try again after some time."),
      err(1020, "Create Failed", "Aw! Snap!, We have encountered an error creating your order. Please try again after some time."),
      err(500, "Server Error", "Internal Server Error"),
    ],
    notes: [
      "Date format is YYYY-MM-DD HH:mm:ss.",
      "If payment_type is COD, cod_amount is mandatory (and vice versa).",
      "The order appears on the merchant panel in realtime. Status events are also posted to web_hook_url.",
      "Recipients get an SMS tracking link.",
      "driver_info is empty until a driver accepts — then it is sent via webhook.",
    ],
  },

  {
    id: "list-order",
    name: "List Order",
    method: "POST",
    endpoint: "/getorder",
    description:
      "Fetches one order by ID, or a paginated list (max 20) filtered by status or branch. Orders are sorted by creation time.",
    headers: [bearerHeader, jsonHeader],
    body: [
      tokenField,
      { field: "order_id", type: "string", required: false, description: "Get one order by internal ID. Do not send offset, limit, display_book_id, or status.", example: "629f2fe68fc53a6e738b0dbc" },
      { field: "display_book_id", type: "string", required: false, description: "Get one order by display ID. Do not send offset, limit, order_id, or status.", example: "WD-1001" },
      { field: "branch_id", type: "string", required: false, description: "List orders for one branch. Do not send order_id, display_book_id, or status. limit and offset are required.", example: "1d3c05a0-cf57-11ec-b00f-65a3fce43a38" },
      { field: "status", type: "string", required: false, description: "List by status. Do not send order_id or display_book_id. limit and offset are required. PENDING, SEARCHING, ASSIGN, UNASSIGN, ACCEPT, ARRIVED AT RESTAURANT, ONROUTE, ARRIVED AT CUSTOMER, COMPLETED, CANCELLED.", example: "COMPLETED" },
      { field: "limit", type: "integer", required: false, description: "Page size. Min 1, Max 20.", example: "10" },
      { field: "offset", type: "integer", required: false, description: "Records to skip for pagination.", example: "0" },
    ],
    requestExample: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy",
  "status": "COMPLETED",
  "limit": 10,
  "offset": 0
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Success",
        example: `{
  "status": true,
  "status_code": 200,
  "data": {
    "total": 24,
    "limit": 10,
    "offset": 0,
    "orders": [
      {
        "order_id": "629f2fe68fc53a6e738b0dbc",
        "display_book_id": "WD-1001",
        "status": "COMPLETED",
        "amount": "KWD 1.750",
        "order_number": "1321",
        "order_details": "PIZZA",
        "customer_info": [
          { "name": "Jack", "country_code": "+965", "phone": "1234567890", "email": "jack@example.com" }
        ],
        "pick_info": { "lat": 29.3326, "long": 48.0719, "address": "Salmiya Branch" },
        "drop_info": { "lat": 29.3759, "long": 47.9774, "address": "Kuwait City, Kuwait" }
      }
    ]
  }
}`,
      },
      err(1002, "Inactive Restaurant", "Your restaurant is currently disable, Please contact our support team for more details."),
      err(1003, "Inactive Branch", "Your branch is currently disable, Please contact our support team for more details."),
      err(1009, "Get Order Failed", "Get order failed"),
      err(1010, "Cross Limit", "Our offset should request a value range from 1 to 20. Which means you cannot fetch more then 20 orders at a time."),
      err(1014, "Invalid Status", "Sended Status is invalid."),
      err(500, "Server Error", "Internal Server Error"),
    ],
    notes: [
      "Single order: send order_id OR display_book_id only.",
      "Status list: send status + limit + offset. Do not send order_id or display_book_id.",
      "Branch list: send branch_id + limit + offset. Do not send order_id, display_book_id, or status.",
      "Plain list: send limit + offset only (status and branch_id optional to omit).",
      "Maximum 20 orders per request.",
    ],
  },

  {
    id: "edit-order",
    name: "Edit Order",
    method: "POST",
    endpoint: "/editorder",
    description:
      "Updates a pending order: drop address, customer details, driver notes, custom order ID, and pickup time. Ongoing (accepted / arrived at restaurant / on trip) and completed orders cannot be edited.",
    headers: [bearerHeader, jsonHeader],
    body: [
      tokenField,
      { field: "order_id", type: "string", required: true, description: "Order ID to edit.", example: "629f2fe68fc53a6e738b0dbc" },
      { field: "lat", type: "number", required: false, description: "Updated drop latitude. If sent, long and address are required.", example: "29.3759" },
      { field: "long", type: "number", required: false, description: "Updated drop longitude. If sent, lat and address are required.", example: "47.9774" },
      { field: "address", type: "string", required: false, description: "Updated drop address. If sent, lat and long are required.", example: "Kuwait City, Kuwait" },
      { field: "pickup_date_time", type: "string", required: false, description: 'Updated pickup time. Format: "YYYY-MM-DD HH:mm:ss".', example: "2026-09-18 18:00:00" },
      { field: "order_number", type: "string", required: false, description: "Updated external order number.", example: "ABC1321" },
      { field: "order_details", type: "string", required: false, description: "Updated order notes for the driver.", example: "MARGARITA PIZZA" },
      { field: "user_name", type: "string", required: false, description: "Updated customer name.", example: "Jack" },
      { field: "user_email", type: "string", required: false, description: "Updated customer email.", example: "jack@example.com" },
      { field: "user_phone", type: "string", required: false, description: "Updated customer phone.", example: "1234567890" },
      { field: "country_code", type: "string", required: false, description: "Updated country code.", example: "+965" },
    ],
    requestExample: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy",
  "order_id": "629f2fe68fc53a6e738b0dbc",
  "order_number": "ABC1321",
  "order_details": "MARGARITA PIZZA",
  "user_name": "Jack",
  "user_email": "jack@example.com",
  "pickup_date_time": "2026-09-18 18:00:00"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Order Updated",
        example: `{
  "status": true,
  "status_code": 200,
  "data": {
    "order_id": "629f2fe68fc53a6e738b0dbc",
    "display_book_id": "WD-1001",
    "status": "PENDING",
    "order_number": "ABC1321",
    "order_details": "MARGARITA PIZZA"
  }
}`,
      },
      err(1002, "Inactive Restaurant", "Your restaurant is currently disable, Please contact our support team for more details."),
      err(1003, "Inactive Branch", "Your branch is currently disable, Please contact our support team for more details."),
      err(1004, "Out of Branch Area", "Unfortunately we don't offer delivery to this area for this branch. Please contact our support team for more details."),
      err(1005, "Delivery Unavailable", "Unfortunately we don't deliver in this area. Please contact our support team for more details."),
      err(1006, "Restricted Area", "The delivery address is restricted. We don't serve in that area."),
      err(1007, "No Delivery Cars", "Currently we don't have valid delivery car's for your delivery address."),
      err(1015, "Order Ongoing", "Your order is ongoing OR order is not available."),
      err(1016, "Edit Failed", "Aw! Snap!, Edit Order Failed."),
      err(500, "Server Error", "Internal Server Error"),
    ],
    notes: [
      "Only PENDING orders can be edited.",
      "Accepted, arrived at restaurant, on trip, and completed orders are locked.",
      "If you send lat, you must also send long and address.",
      "An edit event is posted to your webhook URL.",
    ],
  },

  {
    id: "cancel-order",
    name: "Cancel Order",
    method: "POST",
    endpoint: "/cancelorder",
    description:
      "Cancels an order. After the driver has arrived at the restaurant, cancellation may attract charges. Cannot cancel once the order is ARRIVED AT CUSTOMER.",
    headers: [bearerHeader, jsonHeader],
    body: [
      tokenField,
      { field: "order_id", type: "string", required: true, description: "Order ID to cancel.", example: "629f2fe68fc53a6e738b0dbc" },
    ],
    requestExample: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy",
  "order_id": "629f2fe68fc53a6e738b0dbc"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Order Cancelled",
        example: `{
  "status": true,
  "status_code": 200,
  "data": {
    "order_id": "629f2fe68fc53a6e738b0dbc",
    "status": "CANCELLED",
    "cancellation_charges": 0.000
  }
}`,
      },
      err(1002, "Inactive Restaurant", "Your restaurant is currently disable, Please contact our support team for more details."),
      err(1003, "Inactive Branch", "Your branch is currently disable, Please contact our support team for more details."),
      err(1011, "Cannot Cancel", "Due to some reasons we can't cancel the order."),
      err(1012, "Admin Inactive", "Restaurant admin user is inactive OR not available admin user."),
      err(1013, "Arrived At Customer", "Cancel order Failed Because this order is ARRIVED AT CUSTOMER."),
      err(500, "Server Error", "Internal Server Error"),
    ],
    notes: [
      "Cancelling a pending order deletes it. The webhook receives only order_id and status — not the full order model.",
      "Cancelling SEARCHING, ASSIGN, UNASSIGN, ACCEPT, ARRIVED AT RESTAURANT, or ONROUTE sends the full order model on the webhook.",
      "Cancellation after driver arrival at the restaurant may include charges.",
    ],
  },

  {
    id: "balance",
    name: "Get Restaurant Balance",
    method: "POST",
    endpoint: "/getbalance",
    description: "Returns the restaurant's current wallet balance.",
    headers: [bearerHeader, jsonHeader],
    body: [tokenField],
    requestExample: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Success",
        example: `{
  "status": true,
  "status_code": 200,
  "data": {
    "balance": 125.5,
    "currency": "KWD"
  }
}`,
      },
      err(1002, "Inactive Restaurant", "Your restaurant is currently disable, Please contact our support team for more details."),
      err(1018, "Balance Failed", "Aw! Snap! We got the restaurant wallet balance failed."),
      err(401, "Unauthorized", "This request is not authorized"),
      err(500, "Server Error", "Internal Server Error"),
    ],
    notes: [
      "Create Order returns 1008 when wallet balance is too low.",
      "Recharge the restaurant wallet before dispatching more orders.",
    ],
  },
];
